import { supabase } from './supabase';
import { logger } from './logger';

export interface ReferralStats {
  code: string | null;
  total_referrals: number;
  completed_referrals: number;
  pending_referrals: number;
  months_earned: number;
}

export interface Referral {
  id: string;
  referrer_id: string;
  referee_id: string;
  referral_code: string;
  status: 'pending' | 'completed' | 'rewarded';
  created_at: string;
  completed_at: string | null;
}

const REFERRAL_CODE_KEY = 'vzmo_referral_code';

export function storeReferralCode(code: string): void {
  if (code && typeof window !== 'undefined') {
    sessionStorage.setItem(REFERRAL_CODE_KEY, code.toUpperCase());
  }
}

export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(REFERRAL_CODE_KEY);
}

export function clearStoredReferralCode(): void {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(REFERRAL_CODE_KEY);
  }
}

export async function generateReferralCode(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.rpc('generate_referral_code', {
      p_user_id: user.id
    });

    if (error) {
      logger.error('Error generating referral code:', error);
      return null;
    }

    return data;
  } catch (error) {
    logger.error('Error generating referral code:', error);
    return null;
  }
}

export async function processReferral(userId: string, referralCode: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('process_referral', {
      p_referee_id: userId,
      p_referral_code: referralCode.toUpperCase()
    });

    if (error) {
      logger.error('Error processing referral:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    logger.error('Error processing referral:', error);
    return false;
  }
}

export async function getReferralStats(): Promise<ReferralStats | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.rpc('get_referral_stats', {
      p_user_id: user.id
    });

    if (error) {
      logger.error('Error getting referral stats:', error);
      return null;
    }

    return data as ReferralStats;
  } catch (error) {
    logger.error('Error getting referral stats:', error);
    return null;
  }
}

export async function getReferralHistory(): Promise<Referral[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error getting referral history:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('Error getting referral history:', error);
    return [];
  }
}

export function getReferralLink(code: string): string {
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://vzmo.app';
  return `${baseUrl}/signup?ref=${code}`;
}

export async function subscribeToNewsletter(
  email: string,
  source: string,
  utmParams?: { source?: string; medium?: string; campaign?: string }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('email_subscribers')
      .insert({
        email: email.toLowerCase().trim(),
        source,
        utm_source: utmParams?.source,
        utm_medium: utmParams?.medium,
        utm_campaign: utmParams?.campaign
      });

    if (error) {
      if (error.code === '23505') {
        return { success: true };
      }
      logger.error('Error subscribing to newsletter:', error);
      return { success: false, error: 'Failed to subscribe. Please try again.' };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error subscribing to newsletter:', error);
    return { success: false, error: 'Failed to subscribe. Please try again.' };
  }
}
